import React, { useState } from "react";
import { Icon } from "./BrandIcons";
import Seo from "../components/Seo";
import { ROUTE_META, SITE_URL, breadcrumbSchema } from "../seo/siteMeta.mjs";

const aboutSchema = [
  {
    "@type": "AboutPage",
    name: ROUTE_META["/about"].title,
    description: ROUTE_META["/about"].description,
    url: `${SITE_URL}/about`,
    mainEntity: { "@id": `${SITE_URL}/#organization` },
  },
  breadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
  ]),
];

const values = [
  { icon: "empower", title: "Empower", text: "Equipping organizations with the knowledge, tools, and training to manage their own security." },
  { icon: "collaboration", title: "Collaboration", text: "Partnering closely with clients and industry leaders to build collective resilience." },
  { icon: "integrity", title: "Integrity", text: "Operating with total honesty, ethics, and transparency in every interaction." },
  { icon: "innovation", title: "Innovation", text: "Adapting to new technologies to stay ahead of modern cyber threats." },
  { icon: "excellence", title: "Excellence", text: "Delivering top-quality security solutions and personalized client service." },
];

const apart = [
  { title: "Focus on What Matters Most", body: "At Dark Defensive, we prioritize what matters most—your peace of mind. We understand that the security of your digital assets and sensitive information is paramount. That's why we focus on delivering tangible results that protect your organization's reputation, customer trust, and bottom line." },
  { title: "Expertise", body: "Our team consists of highly skilled cybersecurity professionals with years of industry experience and a proven track record of delivering exceptional results." },
  { title: "Customized Solutions", body: "We understand that every organization is unique, which is why we offer personalized solutions tailored to meet your specific needs, challenges, and goals." },
  { title: "Proactive Approach", body: "We take a proactive approach to cybersecurity, leveraging advanced technologies and methodologies to anticipate and mitigate potential threats before they can impact your business." },
  { title: "Commitment to Excellence", body: "We are committed to excellence in everything we do, from the quality of our services to the level of support we provide to our clients." },
  { title: "Comprehensive Risk Assessment", body: "Understanding your vulnerabilities is the first step to effective cybersecurity. Our comprehensive risk assessment services help you identify and prioritize potential threats, vulnerabilities, and risks to your organization's digital assets and operations. By conducting thorough assessments, we provide actionable insights that enable you to make informed decisions and allocate resources effectively." },
];

const AboutArt = () => (
  <svg className="about-art" viewBox="0 0 520 380" role="img" aria-labelledby="aboutArtTitle aboutArtDesc">
    <title id="aboutArtTitle">Dark Defensive platform illustration</title>
    <desc id="aboutArtDesc">An open laptop with a glowing purple security shield rising from the screen and a fingerprint at its centre, representing identity-based protection and continuous cyber defence.</desc>
    <defs>
      <linearGradient id="ddShield" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#C06BFF" /><stop offset="100%" stopColor="#5A2FA8" />
      </linearGradient>
      <linearGradient id="ddScreen" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#22143F" /><stop offset="100%" stopColor="#120A24" />
      </linearGradient>
      <linearGradient id="ddBase" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#B9A8D8" /><stop offset="100%" stopColor="#7C6AA0" />
      </linearGradient>
      <radialGradient id="ddGlow" cx="50%" cy="45%" r="50%">
        <stop offset="0%" stopColor="#B47BFF" stopOpacity=".55" /><stop offset="100%" stopColor="#B47BFF" stopOpacity="0" />
      </radialGradient>
    </defs>
    <ellipse cx="260" cy="170" rx="200" ry="150" fill="url(#ddGlow)" />
    <rect x="120" y="60" width="280" height="190" rx="12" fill="url(#ddScreen)" stroke="#7A5FB5" strokeWidth="2" />
    <path d="M96 250h328l30 34H66z" fill="url(#ddBase)" />
    <rect x="215" y="262" width="90" height="7" rx="3.5" fill="#5C4A85" />
    <path d="M260 84c34 20 62 26 78 27 0 66-24 108-78 132-54-24-78-66-78-132 16-1 44-7 78-27z" fill="url(#ddShield)" stroke="#E2CBFF" strokeWidth="3" />
    <g fill="none" stroke="#F3E8FF" strokeWidth="3" strokeLinecap="round" opacity=".92">
      <path d="M260 128c-16 0-28 12-28 27v22" />
      <path d="M260 128c16 0 28 12 28 27v14" />
      <path d="M245 155a15 15 0 0 1 30 0v30" />
      <path d="M260 148a8 8 0 0 0-8 8v34" />
      <path d="M260 148a8 8 0 0 1 8 8v26" />
      <path d="M232 190v10" /><path d="M288 178v12" />
    </g>
    <g fill="#C6A8F7">
      <circle cx="404" cy="120" r="3" /><circle cx="428" cy="176" r="2.4" /><circle cx="112" cy="196" r="2.4" /><circle cx="92" cy="128" r="3" />
    </g>
  </svg>
);

const About = () => {
  const [open, setOpen] = useState(null);

  return (
    <div className="dd-page" data-page="about">
      <Seo
        path="/about"
        title={ROUTE_META["/about"].title}
        description={ROUTE_META["/about"].description}
        schema={aboutSchema}
      />

      <div className="wrap section">
        <div className="about-grid">
          <div className="about-copy">
            <h1 className="accent">About Us</h1>
            <p>At Dark Defensive&reg;, we believe cybersecurity, governance, risk, and compliance should empower organizations&mdash;not overwhelm them.</p>
            <p>We founded Dark Defensive because we've lived the challenges firsthand. Our team has spent decades leading enterprise cybersecurity, application security, cloud security, governance, risk management, compliance, and incident response programs across global organizations. We've experienced the complexity of fragmented tools, manual audits, disconnected risk data, and the pressure of making critical decisions with incomplete information. That experience shaped our mission: build the platform we wished we had.</p>
            <p>Dark Defensive is more than a software company. We are practitioners, architects, engineers, and security leaders who understand what it takes to protect modern organizations while enabling business growth. Every capability we build is designed to solve real operational challenges&mdash;not simply add another dashboard or generate more alerts. Our intelligent Cyber GRC platform combines continuous evidence collection, business-driven risk management, critical asset intelligence, and independently validated threat intelligence to help organizations strengthen resilience, reduce audit burden, and make informed, risk-based decisions with confidence.</p>
            <p>We don't believe in selling technology for technology's sake. We believe in building long-term partnerships founded on trust, transparency, and measurable outcomes. Our success is defined by our customers' ability to operate with greater assurance, resilience, and confidence in an increasingly complex threat landscape.</p>
          </div>
          <div>
            <AboutArt />
            <figure className="pull-quote">
              <span className="mark open" aria-hidden="true">&ldquo;</span>
              <blockquote>
                <p>Built by practitioners. Proven through experience. Trusted to help organizations make smarter security and risk decisions through continuous control automation, evidence-driven governance, and proactive audit readiness&mdash;reducing manual effort, lowering compliance burden, and giving teams back valuable time to focus on what matters most.</p>
              </blockquote>
              <span className="mark close" aria-hidden="true">&rdquo;</span>
            </figure>
          </div>
        </div>

        <div className="mv-grid">
          <div>
            <h2>Our Mission</h2>
            <p>To be the trusted global provider of GRC platform services for intelligent cyber risk management, compliance automation, and operational resilience, empowering organizations to thrive across evolving regulations, industry standards, and business operations.</p>
          </div>
          <div>
            <h2>Our Vision</h2>
            <p>We empower organizations to modernize cyber risk management by harnessing intelligent automation, enabling proactive defense, and driving data-driven decision-making.</p>
          </div>
        </div>
      </div>

      <div className="band band-circuit">
        <div className="wrap statement">
          <p>Through proactive risk management, advanced threat detection, and strategic consulting services, we strive to safeguard our clients&rsquo; digital assets and uphold the integrity of their operations.</p>
        </div>

        <div className="wrap section section--tight">
          <h2 style={{ textAlign: "center" }}>Our <span className="accent">Values</span></h2>
          <div className="values-grid">
            {values.map((v) => (
              <article className="value" key={v.title}>
                <span className="icon"><Icon name={v.icon} /></span>
                <h3>{v.title}</h3>
                <p>{v.text}</p>
              </article>
            ))}
          </div>
        </div>
      </div>

      <div className="wrap section">
        <h2>What Sets Us <span className="accent">Apart</span></h2>
        <div className="accordion">
          {apart.map((item, i) => (
            <div className="acc-item" key={item.title}>
              <h3 style={{ margin: 0 }}>
                <button
                  className="acc-trigger"
                  type="button"
                  aria-expanded={open === i}
                  onClick={() => setOpen(open === i ? null : i)}
                >
                  <span>{item.title}</span>
                  <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                    <path d="M5 9l7 7 7-7" />
                  </svg>
                </button>
              </h3>
              {open === i && (
                <div className="acc-panel">
                  <p>{item.body}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default About;
